import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { Supplier } from "../models/Supplier";

interface FormErrors {
	Name?: string;
}

interface SupplierDetailViewerProps {
	supplier: Supplier | null;
	existingSuppliers: Supplier[];
	isSaving: boolean;
	onSave: (supplier: Supplier) => void;
	onClose: () => void;
}

export const SupplierDetailViewer = ({
	supplier,
	existingSuppliers,
	isSaving,
	onSave,
	onClose,
}: SupplierDetailViewerProps) => {
	const [formData, setFormData] = useState<Supplier | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewSupplier = !supplier?.Id;

	const validateForm = useMemo(() => {
		return (data: Supplier | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingSuppliers.some(
					(s) =>
						s.Id !== data.Id &&
						s.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingSuppliers]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewSupplier && isFormValid;

	useEffect(() => {
		if (supplier) {
			setFormData(sanitizeNullStrings({ ...supplier }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [supplier]);

	const handleChange = (field: keyof Supplier, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof Supplier) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof Supplier, value: unknown) => {
		handleBlur(field);

		if (isNewSupplier) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = supplier?.[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCheckboxChange = (field: keyof Supplier, value: unknown) => {
		handleChange(field, value);

		if (isNewSupplier) return;

		if (supplier?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!supplier) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a supplier to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewSupplier ? "New Supplier" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewSupplier && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1.5 }} />

			<Grid2 container spacing={1.5}>
				{/* Basic Information */}
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
						}}
					>
						Basic Information
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Short Name"
						value={formData.ShortName ?? ""}
						onChange={(e) => handleChange("ShortName", e.target.value)}
						onBlur={(e) => handleBlurSave("ShortName", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Number"
						value={formData.Number ?? ""}
						onChange={(e) => handleChange("Number", e.target.value)}
						onBlur={(e) => handleBlurSave("Number", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				{/* Address */}
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
							mt: 1,
						}}
					>
						Address
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Address 1"
						value={formData.Address1 ?? ""}
						onChange={(e) => handleChange("Address1", e.target.value)}
						onBlur={(e) => handleBlurSave("Address1", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Address 2"
						value={formData.Address2 ?? ""}
						onChange={(e) => handleChange("Address2", e.target.value)}
						onBlur={(e) => handleBlurSave("Address2", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Address 3"
						value={formData.Address3 ?? ""}
						onChange={(e) => handleChange("Address3", e.target.value)}
						onBlur={(e) => handleBlurSave("Address3", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Address 4"
						value={formData.Address4 ?? ""}
						onChange={(e) => handleChange("Address4", e.target.value)}
						onBlur={(e) => handleBlurSave("Address4", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="City"
						value={formData.City ?? ""}
						onChange={(e) => handleChange("City", e.target.value)}
						onBlur={(e) => handleBlurSave("City", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="State"
						value={formData.State ?? ""}
						onChange={(e) => handleChange("State", e.target.value)}
						onBlur={(e) => handleBlurSave("State", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Zip"
						value={formData.Zip ?? ""}
						onChange={(e) => handleChange("Zip", e.target.value)}
						onBlur={(e) => handleBlurSave("Zip", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Country"
						value={formData.Country ?? ""}
						onChange={(e) => handleChange("Country", e.target.value)}
						onBlur={(e) => handleBlurSave("Country", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				{/* Contact Information */}
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
							mt: 1,
						}}
					>
						Contact Information
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Phone"
						value={formData.Phone ?? ""}
						onChange={(e) => handleChange("Phone", e.target.value)}
						onBlur={(e) => handleBlurSave("Phone", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Fax"
						value={formData.Fax ?? ""}
						onChange={(e) => handleChange("Fax", e.target.value)}
						onBlur={(e) => handleBlurSave("Fax", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Contact Name"
						value={formData.Contact ?? ""}
						onChange={(e) => handleChange("Contact", e.target.value)}
						onBlur={(e) => handleBlurSave("Contact", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Contact Name 2"
						value={formData.ContactName2 ?? ""}
						onChange={(e) => handleChange("ContactName2", e.target.value)}
						onBlur={(e) => handleBlurSave("ContactName2", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				{/* Active Checkbox */}
				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
